import { LitElement, html, css } from "lit";
import type { TemplateResult } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import "@wh-web-front-end/wh-themes/dist/web/walgreens/light.css";
import { localized } from "@lit/localize";
import type { RouterLocation } from "@vaadin/router";

import { Router } from "./routes/router";
import "./web-kiosk-footer";
import { setLocale } from "./localization";
import ActivityMonitor from "./utilities/activity-monitor.utility";
import "./elements/web-kiosk-timeout-modal";
import "./elements/web-kiosk-end-session-prompt";
import type { WebKioskEndSessionPromptElement } from "./elements/web-kiosk-end-session-prompt";
import { ClockController } from "./elements/clock-controller";
import {
  getPayorAndLocale,
  getRoute,
  getSearch,
  getStore,
} from "./utilities/navigation.utility";
import { languageClient } from "./services/language-service";
import type {
  EndSessionPromptContent,
  FooterContent,
  InactivityModalContent,
} from "./types/content";

const prefix = process.env.URL_PATH_PREFIX ?? "";

declare global {
  interface HTMLElementTagNameMap {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    "web-kiosk": WebKioskElement;
  }
}

@localized()
@customElement("web-kiosk")
export class WebKioskElement extends LitElement {
  @state() footerContent: FooterContent | undefined = undefined;

  @state() timeoutContent: InactivityModalContent | undefined = undefined;

  @state() endSessionPromptContent: EndSessionPromptContent | undefined =
    undefined;

  payor = "default";

  locale = "en";

  location: RouterLocation | undefined;

  @query("web-kiosk-footer") footer: HTMLElement | undefined;

  @query("web-kiosk-end-session-prompt") endSessionPrompt:
    | WebKioskEndSessionPromptElement
    | undefined;

  private readonly activityMonitor: ActivityMonitor;

  private readonly allowedInactiveTime = 160;

  private readonly clock = new ClockController(this, this.allowedInactiveTime);

  constructor() {
    super();

    this.activityMonitor = new ActivityMonitor();
    this.activityMonitor.addEventListener("user-activity", () => {
      this.clock.remainingSeconds = this.allowedInactiveTime;
    });
  }

  connectedCallback(): void {
    super.connectedCallback();
  }

  firstUpdated() {
    window.addEventListener("activity-has-timed-out", () => {
      this.openVideo();
    });
  }

  updated(): void {
    if (this.location) {
      const parameters = getPayorAndLocale(this.location);

      this.payor = parameters.payor;
      this.locale = parameters.locale;

      this.footerContent = this.getContentForElement("footer");
      this.timeoutContent = this.getContentForElement("inactivityModal");
      this.endSessionPromptContent =
        this.getContentForElement("endSessionPrompt");
    }
  }

  getContentForElement(key: string) {
    return languageClient.fetchContentEntry(this.locale, this.payor, key);
  }

  toggleFooter(hide: boolean): void {
    this.footer?.classList.toggle("hidden", hide);
  }

  showEndSessionButton(): boolean {
    if (!this.location || this.location.route === null) {
      return false;
    }

    const noShowRoutes = ["new-session", "video"];
    const currentRoute = getRoute(this.location);

    if (currentRoute === undefined) {
      return false;
    }

    return !noShowRoutes.includes(currentRoute);
  }

  openVideo(): void {
    if (!this.location) {
      return;
    }

    const parameters = getPayorAndLocale(this.location);
    const search = getSearch(this.location);
    const store = getStore(search);
    const currentRoute = getRoute(this.location);

    if (currentRoute !== "video") {
      const path = `${parameters.payor}/${parameters.locale}/video${store}`;

      this.setLocation(path);
    }
  }

  closeVideo(): void {
    this.toggleFooter(false);

    if (this.location === undefined) {
      throw new Error(
        "Search was undefined. Closing the video and rerouting to the home page will not work without a valid search param"
      );
    }
    const search = getSearch(this.location);

    const enHomeRoute = `${prefix}/default/en/new-session${search}`;

    this.setLocation(enHomeRoute);
  }

  setLocation(location: string): void {
    Router.go(location);
  }

  isModalOpen(): boolean {
    return this.clock.remainingSeconds > 0 && this.clock.remainingSeconds <= 20;
  }

  closeTimeOutModel(): void {
    this.clock.remainingSeconds = 0;
  }

  endSession(): void {
    this.openVideo();
    this.closeTimeOutModel();
  }

  openEndSessionPrompt(): void {
    this.endSessionPrompt?.openPrompt();
  }

  closeEndSessionPrompt(): void {
    this.endSessionPrompt?.closePrompt();
  }

  endSessionThroughPrompt(): void {
    this.openVideo();
    this.closeEndSessionPrompt();
  }

  async changeLocale(locale: string): Promise<void> {
    if (!this.location) {
      return;
    }

    const parameters = getPayorAndLocale(this.location);
    const route = getRoute(this.location);
    if (route === undefined) {
      throw new Error(
        "Route was undefined. Changing locale will not work without a valid route"
      );
    }
    const search = getSearch(this.location);
    const url = `${prefix}/${parameters.payor}/${locale}/${route}${search}`;

    this.setLocation(url);

    await setLocale(locale);
  }

  navigate(path: string): void {
    if (!this.location) {
      return;
    }

    const parameters = getPayorAndLocale(this.location);
    const search = getSearch(this.location);
    const store = getStore(search);

    const url = `${prefix}/${parameters.payor}/${parameters.locale}/${path}${store}`;

    this.setLocation(url);
  }

  render(): TemplateResult {
    return html`
      <section>
        <main
          @web-kiosk-video-open=${() => {
            this.toggleFooter(true);
          }}
          @web-kiosk-video-close=${() => {
            this.closeVideo();
          }}
        >
          <slot></slot>
          <web-kiosk-timeout-modal
            .content=${this.timeoutContent}
            .open=${this.isModalOpen()}
            .timeoutNumber=${this.clock.remainingSeconds}
            .payor=${this.payor}
            .locale=${this.locale}
            @leave=${() => {
              this.endSession();
            }}
          ></web-kiosk-timeout-modal>
        </main>
        <web-kiosk-end-session-prompt
          .content=${this.endSessionPromptContent}
          @end-session=${() => {
            this.endSessionThroughPrompt();
          }}
          @resume-session=${() => {
            this.closeEndSessionPrompt();
          }}
        ></web-kiosk-end-session-prompt>
        <web-kiosk-footer
          .content=${this.footerContent}
          @web-kiosk-change-locale=${async (event: CustomEvent<string>) => {
            await this.changeLocale(event.detail);
          }}
          @web-kiosk-navigate=${(event: CustomEvent<string>) => {
            this.navigate(event.detail);
          }}
          @open-end-session-prompt=${() => {
            this.openEndSessionPrompt();
          }}
          .showEndSessionButton=${this.showEndSessionButton()}
        ></web-kiosk-footer>
      </section>
    `;
  }

  static styles = css`
    section {
      display: flex;
      flex-flow: column;
      height: 100%;
    }

    main {
      flex: 1 1 auto;
    }

    web-kiosk-footer {
      position: sticky;
      bottom: 0;
    }

    .hidden {
      display: none;
    }
  `;
}
